import type {
  ObserverRole,
  WheelObservableState,
} from '@goodtime/shared';
import { ORIGIN } from './client';

export interface WheelStreamHandlers {
  /** Full state snapshot — fired on first connect, on phase rollover,
   * and as the recovery message after a {@code close} reconnect. */
  onInit?: (view: WheelObservableState) => void;
  /** RFC 6902 JSON Patch ops describing the delta from the prior view.
   * The patcher in {@code useWheelRoomStream} applies these to the
   * cached state in-memory; raw access here is provided for callers
   * that want to log patches. */
  onDelta?: (patch: ReadonlyArray<JsonPatchOp>) => void;
  /** R1.4 — side-band ephemeral reaction events. Fired every time a
   * player or bot emits a REACTION command; the seat avatar overlay
   * uses these to render a float-up emoji. Reactions are NOT part of
   * the observable state — replay-after-reconnect won't re-fire them. */
  onReaction?: (event: ReactionEvent) => void;
  /** Social — fired when a human takes a bot's seat (a JOIN that
   * displaces a bot). The room view renders a transient toast and
   * suppresses the one for the local player's own join. Ephemeral,
   * exactly like reactions — not part of observable state. */
  onSeatTaken?: (event: SeatTakenEvent) => void;
  /** Server is closing the connection — typically on graceful failover.
   * The client should treat the next message as the new owner. */
  onClose?: () => void;
  /** Network or parse error. Distinct from connection-loss reconnects
   * which {@link EventSource} handles transparently. */
  onError?: (event: Event) => void;
}

/** Wire format for a reaction emitted by a seat. Mirrors the server-
 *  side {@code ReactionEmittedEvent} record — keep the field names in
 *  lockstep with the Java type or Jackson serialization will diverge. */
export interface ReactionEvent {
  seatId: number;
  /** One of the v1 gesture ids: {@code high_five / good_luck / cheers}. */
  reactionId: string;
  /** Recipient seat for a directed one-to-one gesture (the client shows
   *  them an "X high-fived you" toast), or {@code null} for a broadcast
   *  gesture to the whole table. */
  targetSeatId: number | null;
  /** Server timestamp at emit. Lets clients drop stale reactions on
   *  reconnect (anything older than the float animation lifetime). */
  emittedAtMs: number;
}

/** Wire format for a human-took-a-seat event. Mirrors the server-side
 *  {@code SeatTakenEvent} record — keep field names in lockstep with the
 *  Java type or Jackson serialization will diverge. */
export interface SeatTakenEvent {
  seatId: number;
  /** The joining player's id — the room view compares this against the
   *  local player id to suppress the toast for your own join. */
  playerId: string;
  /** The joiner's display name (may be null/empty). */
  playerName: string | null;
  /** The displaced bot's display name, e.g. {@code "Brave Bea"}. */
  botName: string;
  /** Server timestamp at emit; lets the client drop a stale toast on a
   *  reconnect. */
  emittedAtMs: number;
}

/** RFC-6902 op shape — re-declared minimally so callers don't have to
 * import from {@code fast-json-patch}. */
export interface JsonPatchOp {
  op: 'add' | 'remove' | 'replace' | 'move' | 'copy' | 'test';
  path: string;
  from?: string;
  value?: unknown;
}

/**
 * Open an SSE stream to a Wheel-of-Fortune room. Server emits three
 * named events ({@code state_init}, {@code state_delta}, {@code close})
 * per the wire format documented on {@code SseRoomBroadcaster}.
 *
 * <p>Returns the {@link EventSource} so the caller can {@code .close()}
 * it on cleanup. Browser auto-reconnect (with {@code Last-Event-ID})
 * is handled by the EventSource API.
 *
 * @param roomId UUID of the wheel room
 * @param role one of {@code 'audience'}, {@code 'player'}, {@code 'debug'}
 *             (lowercase per controller); audience is the default
 * @param playerId required when {@code role === 'player'}
 */
export function subscribeToWheelRoom(
  roomId: string,
  role: ObserverRole = 'AUDIENCE',
  handlers: WheelStreamHandlers = {},
  playerId?: string,
): EventSource {
  const params = new URLSearchParams({ role: role.toLowerCase() });
  if (playerId) params.set('playerId', playerId);
  // Note: this endpoint lives under /api (W6 RoomStreamController),
  // NOT under /v1 (legacy GameRoomController). Don't use the api()
  // helper or eventSourceUrl() — both prepend the /v1 base, which
  // would yield the wrong path. Vite proxy in vite.config.ts has a
  // separate /api → localhost:8092 rule for dev. In production both
  // bases are served from the same origin under VITE_API_BASE_URL.
  const url =
    `${ORIGIN}/api/rooms/${encodeURIComponent(roomId)}/stream?${params.toString()}`;
  const es = new EventSource(url);

  if (handlers.onInit) {
    es.addEventListener('state_init', (event) => {
      try {
        handlers.onInit!(
          JSON.parse((event as MessageEvent).data) as WheelObservableState,
        );
      } catch (e) {
        console.error('[wheelStream] failed to parse state_init', e);
      }
    });
  }

  if (handlers.onDelta) {
    es.addEventListener('state_delta', (event) => {
      try {
        handlers.onDelta!(
          JSON.parse((event as MessageEvent).data) as ReadonlyArray<JsonPatchOp>,
        );
      } catch (e) {
        console.error('[wheelStream] failed to parse state_delta', e);
      }
    });
  }

  if (handlers.onReaction) {
    es.addEventListener('reaction', (event) => {
      try {
        handlers.onReaction!(
          JSON.parse((event as MessageEvent).data) as ReactionEvent,
        );
      } catch (e) {
        console.error('[wheelStream] failed to parse reaction', e);
      }
    });
  }

  if (handlers.onSeatTaken) {
    es.addEventListener('seat_taken', (event) => {
      try {
        handlers.onSeatTaken!(
          JSON.parse((event as MessageEvent).data) as SeatTakenEvent,
        );
      } catch (e) {
        console.error('[wheelStream] failed to parse seat_taken', e);
      }
    });
  }

  if (handlers.onClose) {
    es.addEventListener('close', () => {
      handlers.onClose!();
    });
  }

  if (handlers.onError) {
    es.addEventListener('error', (event) => {
      handlers.onError!(event);
    });
  }

  return es;
}
